import { defineQuery } from "bitecs";
import { CameraTool } from "../bit-components";
import { waitForDOMContentLoaded } from "../utils/async-utils";
import { isMaleAnimationProfile } from "../utils/avatar-utils";
import configManager from "../singleton/ConfigManager";
import { getAvatarRuntimeYawCorrection, resolveAvatarCapabilities } from "../utils/avatar-capabilities";

const IK_HUMANOID_ROLES = {
  leftEye: "leftEye",
  rightEye: "rightEye",
  head: "head",
  neck: "neck",
  leftHand: "leftHand",
  rightHand: "rightHand",
  hips: "hips",
  leftArm: "leftUpperArm",
  rightArm: "rightUpperArm",
  leftForeArm: "leftLowerArm",
  rightForeArm: "rightLowerArm",
  leftFoot: "leftFoot",
  rightFoot: "rightFoot"
};

function findAvatarCapabilitiesRoot(root) {
  let source = root;
  while (source) {
    if (source.userData?.avatarCapabilities) return source;
    source = source.parent;
  }
  return null;
}

export function hasMaleLegacyAnimationProfile(root, fallback) {
  const animations = findAvatarCapabilitiesRoot(root)?.userData?.avatarCapabilities?.animations;
  if (Object.prototype.hasOwnProperty.call(animations || {}, "source")) {
    return animations.source === "legacy-bundled" && animations.profile === "male";
  }
  return fallback(root);
}

function runtimeIKNode(bone) {
  if (!bone) return null;
  const wrapper = bone.parent;
  return wrapper?.name === bone.name && wrapper.children.includes(bone) ? wrapper : bone;
}

function resolveIKBones(root, data) {
  const metadataRoot = findAvatarCapabilitiesRoot(root);
  const configured = Object.fromEntries(
    Object.keys(IK_HUMANOID_ROLES).map(role => [role, root.getObjectByName(data[role])])
  );
  const configuredChest = root.getObjectByName(data.chest);
  if (!metadataRoot && Object.values(configured).every(Boolean) && configuredChest) {
    return { ...configured, chest: configuredChest, hasEyeBones: Boolean(configured.leftEye && configured.rightEye) };
  }

  const bones = resolveAvatarCapabilities(root, [], {
    sourceRoot: metadataRoot || root,
    metadataRoot: metadataRoot || root
  }).humanoid.bones;
  const hasEyeBones = Boolean(bones.leftEye && bones.rightEye);
  return {
    ...Object.fromEntries(
      Object.entries(IK_HUMANOID_ROLES).map(([property, role]) => [
        property,
        metadataRoot
          ? runtimeIKNode(bones[role]) || configured[property]
          : configured[property] || runtimeIKNode(bones[role])
      ])
    ),
    chest: metadataRoot
      ? runtimeIKNode(bones.chest || bones.spine) || configuredChest
      : configuredChest || runtimeIKNode(bones.chest || bones.spine),
    leftEye: metadataRoot
      ? runtimeIKNode(bones.leftEye || bones.head) || configured.leftEye
      : configured.leftEye || runtimeIKNode(bones.leftEye || bones.head),
    rightEye: metadataRoot
      ? runtimeIKNode(bones.rightEye || bones.head) || configured.rightEye
      : configured.rightEye || runtimeIKNode(bones.rightEye || bones.head),
    hasEyeBones
  };
}

const { Vector3, Quaternion, Matrix4, Euler } = THREE;
const Y_AXIS = new Vector3(0, 1, 0);
const IDENTITY_QUATERNION = new Quaternion();

function quaternionAlmostEquals(epsilon, u, v) {
  // Note: q and -q represent same rotation
  return (
    (Math.abs(u.x - v.x) < epsilon &&
      Math.abs(u.y - v.y) < epsilon &&
      Math.abs(u.z - v.z) < epsilon &&
      Math.abs(u.w - v.w) < epsilon) ||
    (Math.abs(-u.x - v.x) < epsilon &&
      Math.abs(-u.y - v.y) < epsilon &&
      Math.abs(-u.z - v.z) < epsilon &&
      Math.abs(-u.w - v.w) < epsilon)
  );
}

export function composeAvatarRootQuaternion(
  target,
  cameraYaw,
  importedRoot,
  preserveImportedRoot,
  runtimeFacing = IDENTITY_QUATERNION
) {
  target.copy(cameraYaw).multiply(runtimeFacing);
  return preserveImportedRoot ? target.multiply(importedRoot) : target;
}

export function applyAvatarHeadRotation(head, avatar, headTransform, invHipsQuaternion, fullBody) {
  if (fullBody) return;
  invHipsQuaternion.copy(avatar.quaternion).invert();
  head.quaternion.setFromRotationMatrix(headTransform).premultiply(invHipsQuaternion);
}

export function alignAvatarEyesToCamera(avatar, leftEye, rightEye, camera, scratch) {
  avatar.updateMatrix();
  avatar.updateWorldMatrix(true, true);
  leftEye.getWorldPosition(scratch.leftEyeWorld);
  rightEye.getWorldPosition(scratch.rightEyeWorld);
  camera.getWorldPosition(scratch.cameraWorld);
  scratch.eyeDelta
    .addVectors(scratch.leftEyeWorld, scratch.rightEyeWorld)
    .multiplyScalar(0.5)
    .sub(scratch.cameraWorld)
    .negate();

  if (avatar.parent) {
    avatar.parent.getWorldQuaternion(scratch.parentWorldQuaternion).invert();
    avatar.parent.getWorldScale(scratch.parentWorldScale);
    scratch.eyeDelta.applyQuaternion(scratch.parentWorldQuaternion).divide(scratch.parentWorldScale);
  }

  avatar.position.add(scratch.eyeDelta);
  avatar.updateMatrix();
}

const cameraToolsQuery = defineQuery([CameraTool]);

/**
 * Provides access to the end effectors for IK.
 * @namespace avatar
 * @component ik-root
 */
AFRAME.registerComponent("ik-root", {
  schema: {
    camera: { type: "string", default: ".camera" },
    leftController: { type: "string", default: ".left-controller" },
    rightController: { type: "string", default: ".right-controller" }
  },
  update(oldData) {
    if (this.data.camera !== oldData.camera) {
      this.camera = this.el.querySelector(this.data.camera);
    }

    if (this.data.leftController !== oldData.leftController) {
      this.leftController = this.el.querySelector(this.data.leftController);
    }

    if (this.data.rightController !== oldData.rightController) {
      this.rightController = this.el.querySelector(this.data.rightController);
    }
  }
});

function findIKRoot(entity) {
  while (entity && !(entity.components && entity.components["ik-root"])) {
    entity = entity.parentNode;
  }
  return entity && entity.components["ik-root"];
}

const HAND_ROTATIONS = {
  left: new Matrix4().makeRotationFromEuler(new Euler(-Math.PI / 2, Math.PI / 2, 0)),
  right: new Matrix4().makeRotationFromEuler(new Euler(-Math.PI / 2, -Math.PI / 2, 0))
};

const angleOnXZPlaneBetweenMatrixRotations = (function () {
  const XZ_PLANE_NORMAL = new THREE.Vector3(0, -1, 0);
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();
  return function angleOnXZPlaneBetweenMatrixRotations(matrixA, matrixB) {
    v1.setFromMatrixColumn(matrixA, 2).projectOnPlane(XZ_PLANE_NORMAL);
    v2.setFromMatrixColumn(matrixB, 2).projectOnPlane(XZ_PLANE_NORMAL);
    return v1.angleTo(v2);
  };
})();

/**
 * Performs IK on a hip-rooted skeleton to align the hip, head and hands with camera and controller inputs.
 * @namespace avatar
 * @component ik-controller
 */
AFRAME.registerComponent("ik-controller", {
  schema: {
    leftEye: { type: "string", default: "LeftEye" },
    rightEye: { type: "string", default: "RightEye" },
    head: { type: "string", default: "Head" },
    neck: { type: "string", default: "Neck" },
    leftHand: { type: "string", default: "LeftHand" },
    rightHand: { type: "string", default: "RightHand" },
    hips: { type: "string", default: "Hips" },
    leftArm: { type: "string", default: "LeftArm" },
    rightArm: { type: "string", default: "RightArm" },
    leftForeArm: { type: "string", default: "LeftForeArm" },
    rightForeArm: { type: "string", default: "RightForeArm" },
    leftFoot: { type: "string", default: "LeftFoot" },
    rightFoot: { type: "string", default: "RightFoot" },
    chest: { type: "string", default: "Spine" },
    rotationSpeed: { default: 8 },
    maxLerpAngle: { default: 90 * THREE.MathUtils.DEG2RAD },
    maxLerpAngleNeck: { default: 60 * THREE.MathUtils.DEG2RAD },
    alwaysUpdate: { type: "boolean", default: false }
  },

  init() {
    this._runScheduledWork = this._runScheduledWork.bind(this);
    this._updateIsInView = this._updateIsInView.bind(this);

    this.flipY = new Matrix4().makeRotationY(Math.PI);

    this.cameraForward = new Matrix4();
    this.headTransform = new Matrix4();
    this.hipsPosition = new Vector3();

    this.invHipsToHeadVector = new Vector3();

    this.middleEyeMatrix = new Matrix4();
    this.middleEyePosition = new Vector3();
    this.eyeAlignmentScratch = {
      leftEyeWorld: new Vector3(),
      rightEyeWorld: new Vector3(),
      cameraWorld: new Vector3(),
      eyeDelta: new Vector3(),
      parentWorldQuaternion: new Quaternion(),
      parentWorldScale: new Vector3()
    };
    this.invMiddleEyeToHead = new Matrix4();

    this.cameraYRotation = new Euler();
    this.cameraYQuaternion = new Quaternion();

    this.avatarRestQuaternion = new Quaternion();
    this.avatarTargetQuaternion = new Quaternion();
    this.avatarRootSource = null;
    this.avatarFacingQuaternion = new Quaternion();
    this.preserveAvatarRestQuaternion = false;
    this.invHipsQuaternion = new Quaternion();
    this.headQuaternion = new Quaternion();

    this.rootToChest = new Matrix4();
    this.invRootToChest = new Matrix4();

    this.ikRoot = findIKRoot(this.el);

    this.isInView = true;
    this.hasConvergedHips = false;
    this.lastCameraTransform = new THREE.Matrix4();
    waitForDOMContentLoaded().then(() => {
      this.playerCamera = document.getElementById("viewing-camera").getObject3D("camera");
    });

    this.el.sceneEl.systems["frame-scheduler"].schedule(this._runScheduledWork, "ik");
    this.forceIkUpdate = true;
    this.fullBody = false;

    const sceneEl = this.el.parentEl;

    const MALE_FOOT_HEIGHT = -0.675;
    const FEMALE_FOOT_HEIGHT = -0.575;
    const MALE_FOOT_HEIGHT_VR = -0.5;
    const FEMALE_FOOT_HEIGHT_VR = -0.3;

    this.hasMaleAnim = hasMaleLegacyAnimationProfile(this.el.object3D, () =>
      isMaleAnimationProfile(sceneEl?.object3DMap?.object3d)
    );
    this.distanceFootHeight = this.hasMaleAnim ? MALE_FOOT_HEIGHT : FEMALE_FOOT_HEIGHT;
    this.distanceFootHeightVR = this.hasMaleAnim ? MALE_FOOT_HEIGHT_VR : FEMALE_FOOT_HEIGHT_VR;
    this.flipX = new Matrix4().makeRotationX(this.hasMaleAnim ? -Math.PI / 6 : -Math.PI / 8);
  },

  remove() {
    this.el.sceneEl.systems["frame-scheduler"].unschedule(this._runScheduledWork, "ik");
  },

  update() {
    const nextAvatar = this.el.object3D;
    if (this.avatarRootSource !== nextAvatar) {
      this.avatarRootSource = nextAvatar;
      this.avatarRestQuaternion.copy(nextAvatar.quaternion);
    }
    this.avatar = nextAvatar;

    const capabilitiesRoot = findAvatarCapabilitiesRoot(this.avatar);
    this.preserveAvatarRestQuaternion = Boolean(capabilitiesRoot);
    this.avatarFacingQuaternion.setFromAxisAngle(
      Y_AXIS,
      getAvatarRuntimeYawCorrection(this.avatar, capabilitiesRoot || this.avatar)
    );
    Object.assign(this, resolveIKBones(this.avatar, this.data));
    this.fullBody = this._hasFoot();

    const avatar = this.el.closest(".model");
    avatar["setAttribute"](
      "position",
      new THREE.Vector3(0, capabilitiesRoot ? 0 : this._hasFoot() ? this.distanceFootHeight : 0, 0)
    );

    this._hands.forEach(hand => this._isNoControllerAndNoArmAvatar(hand) && this._hideHand(hand));

    this.hasIKBones = Boolean(this.head && this.neck && this.chest && this.leftEye && this.rightEye);
    if (this.hasEyeBones) {
      // Set middleEye's position to be right in the middle of the left and right eyes.
      this.middleEyePosition.addVectors(this.leftEye.position, this.rightEye.position);
      this.middleEyePosition.divideScalar(2);
      if (capabilitiesRoot && (this.avatar.scale.x !== 1 || this.avatar.scale.y !== 1 || this.avatar.scale.z !== 1)) {
        this.middleEyePosition.multiply(this.avatar.scale).applyQuaternion(this.avatar.quaternion);
      }
    } else {
      this.middleEyePosition.set(0, 0, 0);
    }
    this.middleEyeMatrix.makeTranslation(this.middleEyePosition.x, this.middleEyePosition.y, this.middleEyePosition.z);
    this.invMiddleEyeToHead = this.middleEyeMatrix.copy(this.middleEyeMatrix).invert();

    this.nameTag = this.ikRoot.el.querySelector('[name-tag=""]');
    const nameTagComponent = this.nameTag?.components?.["name-tag"];
    if (nameTagComponent) nameTagComponent.nametagHeight = this.fullBody && !capabilitiesRoot ? 1.9 : 0;
    if (this.chest && this.neck && this.head && (capabilitiesRoot || !this.fullBody)) {
      if (capabilitiesRoot) {
        this.head.getWorldPosition(this.invHipsToHeadVector);
        this.avatar.worldToLocal(this.invHipsToHeadVector).negate();
      } else {
        this.invHipsToHeadVector.addVectors(this.chest.position, this.neck.position).add(this.head.position).negate();
      }
    }
    this.el.setAttribute("ik-custom-controller", {
      camera: this.ikRoot.data.camera,
      fullBody: this.fullBody,
      hasMaleAnim: this.hasMaleAnim
    });
    this.ikCustomController = this.el.components["ik-custom-controller"];
  },

  alignAvatarEyesToCamera() {
    if (!this.preserveAvatarRestQuaternion || !this.hasEyeBones || !this.avatar || !this.ikRoot) return;
    alignAvatarEyesToCamera(
      this.avatar,
      this.leftEye,
      this.rightEye,
      this.ikRoot.camera.object3D,
      this.eyeAlignmentScratch
    );
  },

  tick(time, dt) {
    if (!this.ikRoot) {
      return;
    }

    const root = this.ikRoot.el.object3D;
    const customIk = this.ikCustomController;
    root.updateMatrices();
    const { camera } = this.ikRoot;

    customIk?.adjustCamera();

    const hasNewCameraTransform = !this.lastCameraTransform.equals(camera.object3D.matrix);

    // Optimization: if the camera hasn't moved and the hips converged to the target orientation on a previous frame,
    // then the avatar does not need any IK this frame.
    //
    // Update in-view avatars every frame, and update out-of-view avatars via frame scheduler.
    if (
      this.hasIKBones &&
      (this.data.alwaysUpdate ||
        this.forceIkUpdate ||
        (this.isInView && (hasNewCameraTransform || !this.hasConvergedHips)))
    ) {
      if (hasNewCameraTransform) {
        this.lastCameraTransform.copy(camera.object3D.matrix);
      }

      const {
        avatar,
        head,
        neck,
        chest,
        cameraForward,
        headTransform,
        invMiddleEyeToHead,
        invHipsToHeadVector,
        flipY,
        flipX,
        cameraYRotation,
        cameraYQuaternion,
        avatarRestQuaternion,
        avatarTargetQuaternion,
        preserveAvatarRestQuaternion,
        invHipsQuaternion,
        avatarFacingQuaternion,
        rootToChest,
        invRootToChest
      } = this;
      // Camera faces the -Z direction. Flip it along the Y axis so that it is +Z.
      cameraForward.multiplyMatrices(camera.object3D.matrix, flipY);
      if (this._hasFoot()) cameraForward.multiplyMatrices(cameraForward, flipX);
      // Compute the head position such that the hmd position would be in line with the middleEye
      headTransform.multiplyMatrices(cameraForward, invMiddleEyeToHead);

      // Then position the avatar such that the head is aligned with headTransform
      // (which positions middleEye in line with the hmd)
      //
      // Note that we position the avatar itself, *not* the hips, since positioning the
      // hips will use vertex skinning to do the root displacement, which results in
      // frustum culling errors since three.js does not take into account skinning when
      // computing frustum culling sphere bounds.
      avatar.matrixNeedsUpdate = true;
      head.matrixNeedsUpdate = true;
      if (!customIk?.lockAvatarRotation) {
        // Animate the hip rotation to follow the Y rotation of the camera with some damping.
        // Allow rotate avatar if is not locked
        cameraYRotation.setFromRotationMatrix(cameraForward, "YXZ");
        avatar.position.setFromMatrixPosition(headTransform).add(invHipsToHeadVector);
      }
      cameraYRotation.x = 0;
      cameraYRotation.z = 0;
      cameraYQuaternion.setFromEuler(cameraYRotation);
      composeAvatarRootQuaternion(
        avatarTargetQuaternion,
        cameraYQuaternion,
        avatarRestQuaternion,
        preserveAvatarRestQuaternion,
        avatarFacingQuaternion
      );

      if (this._hadFirstTick) {
        camera.object3D.updateMatrices();
        avatar.updateMatrices();
        // Note: Camera faces down -Z, avatar faces down +Z
        const yDelta = Math.PI - angleOnXZPlaneBetweenMatrixRotations(camera.object3D.matrixWorld, avatar.matrixWorld);

        if (yDelta > this.data.maxLerpAngle) {
          avatar.quaternion.copy(avatarTargetQuaternion);
        } else {
          avatar.quaternion.slerpQuaternions(
            avatar.quaternion,
            avatarTargetQuaternion,
            (this.data.rotationSpeed * dt) / 1000
          );
        }
        if (yDelta > this.data.maxLerpAngleNeck) {
          head.matrixNeedsUpdate = false;
        }
      } else {
        avatar.quaternion.copy(avatarTargetQuaternion);
      }

      this.hasConvergedHips = quaternionAlmostEquals(0.0001, avatarTargetQuaternion, avatar.quaternion);

      // Full-body animation owns the head. Partial avatars retain legacy head IK.
      applyAvatarHeadRotation(head, avatar, headTransform, invHipsQuaternion, this.fullBody);
      this.alignAvatarEyesToCamera();

      avatar.updateMatrix();
      rootToChest.multiplyMatrices(avatar.matrix, chest.matrix);
      invRootToChest.copy(rootToChest).invert();

      root.matrixNeedsUpdate = true;
      neck.matrixNeedsUpdate = true;
      chest.matrixNeedsUpdate = true;
    }

    const { leftHand, rightHand } = this;
    if (!this.fullBody) {
      if (leftHand) this.updateHand(HAND_ROTATIONS.left, leftHand);
      if (rightHand) this.updateHand(HAND_ROTATIONS.right, rightHand);
    }

    this.forceIkUpdate = false;

    if (!this._hadFirstTick) {
      // Ensure the avatar is not shown until we've done our first IK step, to prevent seeing mis-oriented/t-pose pose or our own avatar at the wrong place.
      this.ikRoot.el.object3D.visible = true;
      if (this.nameTag && this.fullBody && !findAvatarCapabilitiesRoot(this.avatar)) {
        this.nameTag.components["name-tag"].nametagHeight = 1.9;
      }

      this._hadFirstTick = true;
      this.el.emit("ik-first-tick");
    }

    if (configManager.config.invisibleAvatar.enabled) {
      this.ikRoot.el.object3D.visible = false;
      if (this.ikRoot.el.components["local-menu"]) {
        this.ikRoot.el.components["local-menu"].remove();
      }
    }
  },

  updateHand(handRotation, handObject3D) {
    // TODO: This coupling with personal-space-invader is not ideal.
    // There should be some intermediate thing managing multiple opinions about object visibility
    const spaceInvader = handObject3D.el.components["personal-space-invader"];

    if (spaceInvader) {
      // If this hand has an invader, defer to it to manage visibility overall but tell it to hide based upon controller state
      spaceInvader.setAlwaysHidden(true);
    } else {
      handObject3D.visible = false;
    }

    // Optimization: skip IK update if not in view and not forced by frame scheduler
    // if (controllerObject3D.visible && (isInView || this.forceIkUpdate || this.data.alwaysUpdate)) {
    //   handMatrix.multiplyMatrices(this.invRootToChest, controllerObject3D.matrix);

    //   handMatrix.multiply(handRotation);

    //   handObject3D.position.setFromMatrixPosition(handMatrix);
    //   handObject3D.rotation.setFromRotationMatrix(handMatrix);
    //   handObject3D.matrixNeedsUpdate = true;
    // }
  },

  _runScheduledWork() {
    // Every scheduled run, we force an IK update on the next frame (so at most one avatar with forced IK per frame)
    // and also update the this.isInView bit on the avatar which is used to determine if an IK update should be run
    // every frame.
    this.forceIkUpdate = true;

    this._updateIsInView();
  },

  _updateIsInView: (function () {
    const frustum = new THREE.Frustum();
    const frustumMatrix = new THREE.Matrix4();
    const tmpPos = new THREE.Vector3();
    const isInViewOfCamera = (screenCamera, pos) => {
      frustumMatrix.multiplyMatrices(screenCamera.projectionMatrix, screenCamera.matrixWorldInverse);
      frustum.setFromProjectionMatrix(frustumMatrix);
      return frustum.containsPoint(pos);
    };

    return function () {
      if (!this.playerCamera || this.data.alwaysUpdate) return;

      const camera = this.ikRoot.camera.object3D;
      camera.getWorldPosition(tmpPos);

      // Check player camera
      this.isInView = isInViewOfCamera(this.playerCamera, tmpPos);

      if (!this.isInView) {
        const world = APP.world;
        // Check camera tools if they are rendering to viewfinder
        const cameraTools = cameraToolsQuery(world);
        for (const eid of cameraTools) {
          // const screenObj = world.eid2obj.get(CameraTool.screenRef[eid]);
          const cameraObj = world.eid2obj.get(CameraTool.cameraRef[eid]);
          this.isInView = isInViewOfCamera(cameraObj, tmpPos);
          if (this.isInView) break;
        }
      }
    };
  })(),

  /**
   * @private
   * @typedef {"left" | "right"} Hand
   * @type {Hand[]}
   */
  _hands: ["left", "right"],

  /**
   * @private
   * @param {Hand} hand
   * @returns {Object.<string, THREE.Object3D>} - if controller is exists for the given direction.
   */
  _getController(hand) {
    return this.ikRoot[`${hand}Controller`];
  },

  /**
   * @private
   * @param {Hand} hand
   * @returns {THREE.Object3D} - if avatar has hand for the given direction.
   */
  _getHand(hand) {
    return this[`${hand}Arm`];
  },

  /**
   * @private
   * @param {Hand} hand
   * @returns {boolean}
   */
  _existsControllerAndHand(hand) {
    return this._getController(hand).object3D.visible && this._getHand(hand);
  },

  /**
   * @private
   * @param {Hand} hand
   * @returns {boolean}
   */
  _isNoControllerAndNoArmAvatar(hand) {
    return !this.ikRoot[`${hand}Controller`].object3D.visible && !this[`${hand}Arm`];
  },
  _isControllerAndArm(hand) {
    return this.ikRoot[`${hand}Controller`].object3D.visible && this[`${hand}Arm`];
  },

  /**
   * @private
   * @param {Hand} hand
   */
  _hideHand(hand) {
    this[`${hand}Hand`]?.el.setAttribute("visible", false);
  },

  /**
   * @private
   * @returns {boolean}
   */
  _hasFoot() {
    return Boolean(this.leftFoot || this.rightFoot);
  },

  /**
   * @private
   */
  _setHandPosition(hand) {
    this[hand]?.el.setAttribute("position", this[hand]?.position);
  }
});
